import { useMemo } from "react";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { type PortServicesOutletContext } from "../../PortServicesContainer";
import type { PortServicesFormData } from "../../schema/portServicesFormSchema";
import { usePortServiceDocumentUpload } from "../utils";

const LANG_BASE = "vessel-call.port-services";
const FIELD_NAME = "customsDocuments" as const;

const ShipChandlingUploadFields = () => {
  const { t } = useTranslation();
  const { formData, updateFormData } = useOutletContext<PortServicesOutletContext>();
  const direction = formData.watch("direction");
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const { portServicesRequestFile: uploadConditions } = FILE_UPLOAD_PROFILES;

  const { files, setFiles, handleFieldChangeForAzure, removeFileAtIndex, isUploading } =
    usePortServiceDocumentUpload({
      fieldName: FIELD_NAME,
      certificateType: "CUSTOMS_DOCUMENT",
      formData,
      updateFormData,
    });

  const allowedExtensions = useMemo(
    () =>
      (uploadConditions.format ?? []).map((ext) =>
        ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
      ),
    [uploadConditions.format],
  );

  return (
    <FormProvider {...formData}>
      <div className="flex flex-col gap-6" data-testid="ship-chandling-upload-fields">
        {direction === "Outbound" && (
          <FormSection isFullWidth title={t(`${LANG_BASE}.fields.customs-document`)}>
            <div className="relative">
              {isUploading && (
                <div className="absolute inset-0 flex justify-center pointer-events-none top-30 opacity-100">
                  <RotateRightIcon
                    fontSize="large"
                    sx={{
                      "animation": "mui-spin 0.9s linear infinite",
                      "@keyframes mui-spin": { to: { transform: "rotate(360deg)" } },
                    }}
                  />
                </div>
              )}
              <div className={isUploading ? "opacity-50" : ""}>
                <FormItem<PortServicesFormData, "customsDocuments">
                  name={FIELD_NAME}
                  type="file"
                  testId="ship-chandling-customs-documents"
                  uploadConditions={uploadConditions}
                  maxFiles={1}
                  allowedFileExtensions={allowedExtensions}
                  uploadDescription={t("upload.additional-documents-upload", {
                    extensions: uploadConditions.format.join(", "),
                    size: uploadConditions.maxSizeLabel,
                  })}
                  disabled={isUploading}
                  onUpload={handleFieldChangeForAzure}
                  files={files}
                  setFiles={setFiles}
                />
              </div>
            </div>

            {files.length
              ? files.map((file, idx) => (
                  <div
                    className="flex-1"
                    key={`${file.name}-${(file as File).lastModified ?? idx}`}
                  >
                    <SingleFile
                      testId={`${FIELD_NAME}-${idx}`}
                      file={file}
                      currentFileIndex={idx}
                      fieldName={FIELD_NAME}
                      setFiles={() => removeFileAtIndex(idx)}
                    />
                  </div>
                ))
              : null}
          </FormSection>
        )}
      </div>
    </FormProvider>
  );
};

export default ShipChandlingUploadFields;
